import {
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  Filler,
  LinearScale,
  LineElement,
  PointElement,
  Tooltip,
} from 'chart.js'
import { ReactElement, useMemo, useState } from 'react'
import { Bar, Line } from 'react-chartjs-2'
import { SegmentedControl } from '../../../components/SalesReport'
import { colors, SALES_REPORT_FONT } from '../../../components/SalesReport/tokens'
import {
  bucketSeries,
  cumulativeSeries,
  DEFAULT_GRANULARITY,
  FLOW_GRANULARITIES,
  FlowGranularity,
  FlowMode,
  formatClock,
  formatCount,
  formatDayMonth,
  formatDayTime,
  formatRate,
  hourlyBars,
  localOf,
  minuteOf,
  peakIndex,
} from '../report'
import * as S from './styles'

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, BarElement, Filler, Tooltip)

const FONT = { family: SALES_REPORT_FONT, size: 11 }
const MODE_OPTIONS: Array<{ value: FlowMode, label: string }> = [
  { value: 'interval', label: 'Por intervalo' },
  { value: 'cumulative', label: 'Acumulado' },
]
const GRANULARITY_OPTIONS = FLOW_GRANULARITIES.map((value) => ({
  value: String(value),
  label: value === 60 ? '1 h' : `${value} min`,
}))

const multiDay = (labels: string[]): boolean =>
  labels.length > 0 && labels[0].slice(0, 10) !== labels[labels.length - 1].slice(0, 10)

interface FlowChartProps {
  series: Array<[string, number]>
  issued: number | null
}

export function FlowChart({ series, issued }: FlowChartProps): ReactElement {
  const [mode, setMode] = useState<FlowMode>('interval')
  const [granularity, setGranularity] = useState<FlowGranularity>(DEFAULT_GRANULARITY)
  const percent = mode === 'cumulative' && issued !== null && issued > 0

  const points = useMemo(() => {
    const buckets = bucketSeries(series, granularity)
    return mode === 'cumulative' ? cumulativeSeries(buckets, issued) : buckets
  }, [series, granularity, mode, issued])
  const peak = mode === 'interval' ? peakIndex(points) : -1
  const labels = points.map((point) => point.start)
  const showDay = multiDay(labels)

  const subtitle =
    mode === 'interval'
      ? 'Entradas por intervalo de tempo.'
      : percent
        ? 'Percentual acumulado dos ingressos emitidos que já entraram.'
        : 'Total acumulado de entradas.'

  return (
    <S.Card aria-label="Fluxo de entradas">
      <S.CardHead>
        <div>
          <S.CardTitle>Fluxo de entradas</S.CardTitle>
          <S.CardSubtitle>{subtitle}</S.CardSubtitle>
        </div>
        <S.Tools>
          <SegmentedControl ariaLabel="Modo do gráfico" options={MODE_OPTIONS} value={mode} onChange={setMode} />
          <SegmentedControl
            ariaLabel="Intervalo"
            options={GRANULARITY_OPTIONS}
            value={String(granularity)}
            onChange={(next: string) => setGranularity(Number(next) as FlowGranularity)}
          />
        </S.Tools>
      </S.CardHead>
      {points.length === 0 ? (
        <S.Empty>Nenhuma entrada no filtro.</S.Empty>
      ) : (
        <S.ChartBox $height={280}>
          <Line
            aria-label="Gráfico de entradas ao longo do tempo"
            role="img"
            data={{
              labels: labels.map((label) => (showDay ? formatDayTime(label) : formatClock(label))),
              datasets: [
                {
                  data: points.map((point) => Math.round(point.entries * 10) / 10),
                  borderColor: colors.primary,
                  backgroundColor: `rgba(${colors.primaryRgb}, 0.12)`,
                  fill: true,
                  tension: 0.3,
                  borderWidth: 2,
                  pointRadius: points.map((_, index) => (index === peak ? 5 : 0)),
                  pointHoverRadius: 4,
                  pointBackgroundColor: colors.primary,
                },
              ],
            }}
            options={{
              responsive: true,
              maintainAspectRatio: false,
              interaction: { mode: 'index', intersect: false },
              plugins: {
                legend: { display: false },
                tooltip: {
                  callbacks: {
                    title: (items) => formatDayTime(labels[items[0].dataIndex]),
                    label: (item) =>
                      mode === 'interval'
                        ? `${formatCount(item.parsed.y)} entradas`
                        : percent
                          ? `${formatRate(item.parsed.y)}% dos emitidos`
                          : `${formatCount(item.parsed.y)} entradas acumuladas`,
                  },
                },
              },
              scales: {
                x: { grid: { display: false }, ticks: { font: FONT, color: colors.ink500, maxTicksLimit: 10, maxRotation: 0 } },
                y: {
                  beginAtZero: true,
                  max: percent ? 100 : undefined,
                  grid: { color: colors.rowDivider },
                  ticks: {
                    font: FONT,
                    color: colors.ink500,
                    callback: (value) => (percent ? `${String(value)}%` : formatCount(Number(value))),
                  },
                },
              },
            }}
          />
        </S.ChartBox>
      )}
    </S.Card>
  )
}

export function HourlyChart({ series }: { series: Array<[string, number]> }): ReactElement {
  const bars = useMemo(() => hourlyBars(series), [series])
  const peak = peakIndex(bars)
  const labels = bars.map((bar) => bar.start)
  const showDay = multiDay(labels)
  const range = (start: string): string => {
    const end = localOf(minuteOf(start) + 60)
    return `${formatDayMonth(start)} · ${start.slice(11, 13)}h às ${end.slice(11, 13)}h`
  }

  return (
    <S.Card aria-label="Picos por hora">
      <S.CardHead>
        <div>
          <S.CardTitle>Picos por hora</S.CardTitle>
          <S.CardSubtitle>Total de entradas em cada hora cheia. A barra vermelha é a hora de pico.</S.CardSubtitle>
        </div>
      </S.CardHead>
      {bars.length === 0 ? (
        <S.Empty>Nenhuma entrada no filtro.</S.Empty>
      ) : (
        <S.ChartBox $height={240}>
          <Bar
            aria-label="Entradas por hora"
            role="img"
            data={{
              labels: labels.map((label) => (showDay ? `${formatDayMonth(label)} ${label.slice(11, 13)}h` : `${label.slice(11, 13)}h`)),
              datasets: [
                {
                  data: bars.map((bar) => bar.entries),
                  backgroundColor: bars.map((_, index) => (index === peak ? colors.primary : colors.subtotal)),
                  borderRadius: 4,
                },
              ],
            }}
            options={{
              responsive: true,
              maintainAspectRatio: false,
              plugins: {
                legend: { display: false },
                tooltip: {
                  callbacks: {
                    title: (items) => range(labels[items[0].dataIndex]),
                    label: (item) => `${formatCount(item.parsed.y)} entradas · ${formatRate(item.parsed.y / 60)}/min em média`,
                  },
                },
              },
              scales: {
                x: { grid: { display: false }, ticks: { font: FONT, color: colors.ink500, maxRotation: 0, autoSkip: true } },
                y: { beginAtZero: true, grid: { color: colors.rowDivider }, ticks: { font: FONT, color: colors.ink500 } },
              },
            }}
          />
        </S.ChartBox>
      )}
    </S.Card>
  )
}
